import { type FormEvent, useEffect, useState } from 'react';
import { type AiProvider, type AiSettings, type AiSettingsInput, type AiTestResult, api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { dateTimeLabel } from '../../lib/format';
import { Banner, Field, LoadingPage, Tag } from '../../components/ui';
import { PageHeader } from '../../components/PageHeader';
import { AdminNav } from './AdminNav';

const CUSTOM = '__custom';
const PROVIDERS: AiProvider[] = ['gemini', 'anthropic', 'openai'];

interface Form {
  provider: AiProvider;
  baseUrl: string;
  /** A preset id, or CUSTOM to use `customModel`. */
  modelChoice: string;
  customModel: string;
  apiKey: string;
  clearApiKey: boolean;
}

function formFor(settings: AiSettings, provider: AiProvider): Form {
  const current = settings.providers[provider];
  const preset = current.models.includes(current.model);
  return {
    provider,
    baseUrl: current.baseUrl,
    modelChoice: preset ? current.model : CUSTOM,
    customModel: preset ? '' : current.model,
    apiKey: '',
    clearApiKey: false,
  };
}

function toInput(form: Form): AiSettingsInput {
  return {
    provider: form.provider,
    baseUrl: form.baseUrl.trim(),
    model: (form.modelChoice === CUSTOM ? form.customModel : form.modelChoice).trim(),
    ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
    ...(form.clearApiKey ? { clearApiKey: true } : {}),
  };
}

/** Super Admin → Settings: the AI provider and model every AI feature uses. */
export function PlatformSettingsPage() {
  const { notify, fail } = useToast();
  const state = useAsync(() => api.adminAiSettings(), []);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [test, setTest] = useState<AiTestResult | null>(null);

  const settings = state.data;
  useEffect(() => {
    if (settings && !form) setForm(formFor(settings, settings.provider));
  }, [settings, form]);

  if (state.loading && !settings) return <LoadingPage label="Loading settings…" />;
  if (!settings || !form) return <p className="mut">Could not load settings.</p>;

  const info = settings.providers[form.provider];
  const key = info.apiKey;
  const update = (patch: Partial<Form>) => {
    setForm({ ...form, ...patch });
    setTest(null);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy('save');
    try {
      const next = await api.adminSaveAiSettings(toInput(form));
      state.setData(next);
      setForm(formFor(next, form.provider));
      notify(`AI features now use ${next.providers[form.provider].label} · ${next.providers[form.provider].model}.`, 'success');
    } catch (error) {
      fail(error, 'Settings were not saved.');
    } finally {
      setBusy(null);
    }
  };

  const runTest = async () => {
    setBusy('test');
    setTest(null);
    try {
      setTest(await api.adminTestAiSettings(toInput(form)));
    } catch (error) {
      fail(error, 'The test request did not run.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        lede="Choose the AI provider and model behind Bylaws and Architecture chat, document reading, architectural review, the finance assistant and newsletter drafts. Changes apply right away. Super Admin only."
      />
      <AdminNav />

      <form className="card" onSubmit={save}>
        <h3>AI provider</h3>
        <p className="mut">
          In use now: <strong>{settings.providers[settings.provider].label}</strong> ·{' '}
          <code>{settings.providers[settings.provider].model}</code>
          {settings.updatedAt && (
            <>
              {' '}
              — changed {dateTimeLabel(settings.updatedAt)}
              {settings.updatedBy ? ` by ${settings.updatedBy}` : ''}
            </>
          )}
        </p>

        <Field label="Provider" hint="If one provider hits its usage limit, switch to another here.">
          <select
            value={form.provider}
            onChange={(event) => {
              setForm(formFor(settings, event.target.value as AiProvider));
              setTest(null);
            }}
          >
            {PROVIDERS.map((id) => (
              <option key={id} value={id}>
                {settings.providers[id].label}
                {id === settings.provider ? ' (in use)' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="API base URL" hint={`Default: ${info.defaultBaseUrl}`}>
          <input
            type="url"
            value={form.baseUrl}
            onChange={(event) => update({ baseUrl: event.target.value })}
            placeholder={info.defaultBaseUrl}
          />
        </Field>

        <Field label="Model" hint={`Default: ${info.defaultModel}`}>
          <select value={form.modelChoice} onChange={(event) => update({ modelChoice: event.target.value })}>
            {info.models.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
            <option value={CUSTOM}>Custom model id…</option>
          </select>
        </Field>
        {form.modelChoice === CUSTOM && (
          <Field label="Custom model id">
            <input
              value={form.customModel}
              onChange={(event) => update({ customModel: event.target.value })}
              placeholder={info.defaultModel}
              required
            />
          </Field>
        )}

        <Field
          label="API key"
          hint={
            key.source === 'saved'
              ? `A key is saved (${key.hint}). Leave blank to keep it.`
              : key.source === 'environment'
                ? `Using ${key.hint} from the server environment. Enter a key to override it.`
                : 'No key is set for this provider yet.'
          }
        >
          <input
            type="password"
            autoComplete="off"
            value={form.apiKey}
            onChange={(event) => update({ apiKey: event.target.value, clearApiKey: false })}
            placeholder={key.source === 'saved' ? key.hint ?? '' : 'Paste a new key'}
          />
        </Field>
        {key.source === 'saved' && (
          <label className="mut" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={form.clearApiKey}
              onChange={(event) => update({ clearApiKey: event.target.checked, apiKey: '' })}
            />
            Remove the saved key
            {key.envName ? ` and use ${key.envName} from the server environment` : ' (no environment key is set)'}
          </label>
        )}

        {test &&
          (test.ok ? (
            <Banner kind="ok">
              {info.label} answered with <code>{test.model}</code> in {(test.ms / 1000).toFixed(1)}s.
            </Banner>
          ) : (
            <Banner kind="error">
              Test failed <Tag tone="bad">{test.kind}</Tag> {test.error}
            </Banner>
          ))}

        <div className="btn-row">
          <button type="submit" className="btn" disabled={busy !== null}>
            {busy === 'save' ? 'Saving…' : form.provider === settings.provider ? 'Save' : `Save and switch to ${info.label}`}
          </button>
          <button type="button" className="btn s" disabled={busy !== null} onClick={() => void runTest()}>
            {busy === 'test' ? 'Testing…' : 'Test connection'}
          </button>
        </div>
      </form>
    </div>
  );
}
